import { AppShell as MantineAppShell, Text, Anchor, ActionIcon, Tooltip, Image, Group, Burger, Drawer, Stack, NavLink, Box, Divider } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { isPlatformAdmin } from '@teletienda/shared-utils-lib';
import { useComputedColorScheme } from '@mantine/core';
import { useScrolled } from '@/hooks/useScrolled';
import { useTheme } from '@/themes/useTheme';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconShield, IconWallet, IconHeart, IconUser, IconListTree, IconSpeakerphone } from '@tabler/icons-react';
import { MultiCartIcon } from './MultiCartIcon';
import { MessagesHeaderButton } from './MessagesHeaderButton';
import { MobileMenuUserItems } from './MobileMenuUserItems';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SearchBar } from '@/components/ui/SearchBar';
import { useAuth, LoginModal, RegisterModal } from '@teletienda/shared-auth-ui';
import { useOAuthConfig } from '@/providers/PlatformBootstrapProvider';
import {
  HeaderLayout,
  HeaderPreferences,
  HeaderUserSection,
  HeaderNavButton,
  HeaderDivider,
} from '@teletienda/shared-ui-lib';
import { useLanguageSwitcher } from '@/i18n/useLanguageSwitcher';
import { useCurrencyChange } from '@/hooks/useCurrencyChange';
import { type SupportedLanguage } from '@/i18n/config';
import { ReactNode, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores/uiStore';
import { useFavorites } from '@/hooks/useFavorites';
import { FavoritesDrawer } from '@/components/favorites';
import { publicAsset } from '@/utils/images';
import { Footer } from '@/components/layout/Footer';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [loginOpened, { open: openLogin, close: closeLogin }] = useDisclosure(false);
  const [registerOpened, { close: closeRegister }] = useDisclosure(false);
  const [logoError, setLogoError] = useState(false);
  const computedColorScheme = useComputedColorScheme('light');
  // useScrolled stays mounted always (cheap passive scroll listener); the
  // backdrop-blur visual reaction lives only under [data-tt-theme="editorial"]
  // CSS scope, so baseline ignores the data-scrolled attribute entirely.
  const scrolled = useScrolled(20);
  const { themeKey } = useTheme();
  const { user, logout } = useAuth();
  const { facebookAppId: FACEBOOK_APP_ID } = useOAuthConfig();
  const navigate = useNavigate();
  const location = useLocation();
  // The Telegram login leaves the tab entirely, so the page the user opened the modal on
  // has to travel with the request. Without it they come back to `/` and lose their place.
  const oauthReturnUrl = `${location.pathname}${location.search}`;
  const { t } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');
  const { currentLanguage, changeLanguage } = useLanguageSwitcher();
  const { currentCurrency, changeCurrency } = useCurrencyChange();

  // UI Store for FavoritesDrawer
  const { openFavoritesDrawer } = useUIStore();

  // Favorites count for badge
  const { favorites } = useFavorites({ type: 'product' });
  const favoritesCount = favorites.length;

  // Mobile burger menu (collapses the action row below `md`).
  const [menuOpened, { close: closeMenu, toggle: toggleMenu }] = useDisclosure(false);

  // Close the mobile menu on route change so a tapped link never leaves it open.
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Menu item helper: run the action, then close the menu.
  const handleMenuNavigate = useCallback(
    (to: string) => {
      navigate(to);
      closeMenu();
    },
    [navigate, closeMenu]
  );

  // Показывать ли админскую навигацию. RBAC-1.9 AC-4 — набор из общего словаря; третьим
  // именем здесь стоял фантом `platform_admin`, которого нет в базе.
  const isAdmin = isPlatformAdmin(user?.roles);

  // Switch between modals
  const switchToLogin = () => {
    closeRegister();
    openLogin();
  };

  // Handle login success with toast notification
  const handleLoginSuccess = useCallback(() => {
    closeLogin();
    notifications.show({
      title: tAuth('login.success_title'),
      message: tAuth('login.success_message'),
      color: 'green',
      autoClose: 3000,
    });
  }, [closeLogin, tAuth]);

  // Handle logout - navigate home first, then logout
  const handleLogout = useCallback(async () => {
    navigate('/');
    await logout();
    notifications.show({
      title: tAuth('userMenu.logout_success_title'),
      message: tAuth('userMenu.logout_success_message'),
      color: 'blue',
      autoClose: 3000,
    });
  }, [logout, navigate, tAuth]);

  // Handle registration complete (user is logged in)
  const handleRegisterComplete = useCallback(() => {
    closeRegister();
    notifications.show({
      title: tAuth('login.success_title'),
      message: tAuth('login.success_message'),
      color: 'green',
      autoClose: 3000,
    });
  }, [closeRegister, tAuth]);

  return (
    <MantineAppShell header={{ height: 70 }} padding="md">
      <a href="#main-content" className="skip-nav-link">
        {t('skip_to_content', 'Saltar al contenido')}
      </a>
      <MantineAppShell.Header
        aria-label={t('main_navigation', 'Navegación principal')}
        data-scrolled={scrolled || undefined}
      >
        <HeaderLayout testId="header">
          <HeaderLayout.Logo>
            {/* HNR-3: News megaphone sits next to the wordmark as a sibling of
                the logo anchor (platform news ≠ user actions; avoids nested <a>). */}
            <Group gap="xs" wrap="nowrap" align="center">
              <Anchor component={Link} to="/" style={{ textDecoration: 'none' }}>
                <Group gap="xs" wrap="nowrap" align="center">
                  {!logoError ? (
                    <Image
                      src={publicAsset(computedColorScheme === 'dark' ? 'images/logo-white.png' : 'images/logo-black.png')}
                      alt="TeleTienda"
                      h={36}
                      w="auto"
                      onError={() => setLogoError(true)}
                    />
                  ) : null}
                  {/* Wordmark hides below `sm` (576px) to free room for the
                      mobile search box — only the logo mark stays. */}
                  {themeKey === 'editorial' ? (
                    <Text component="span" className="tt-logo-wordmark" visibleFrom="sm">
                      TeleTienda
                    </Text>
                  ) : (
                    <Text size="xl" fw={700} c="brand" visibleFrom="sm">
                      TeleTienda
                    </Text>
                  )}
                </Group>
              </Anchor>
              {/* News megaphone is desktop-inline only; on mobile it lives in
                  the burger menu (below). */}
              <Box visibleFrom="md" style={{ display: 'flex' }}>
                <HeaderNavButton
                  icon={IconSpeakerphone}
                  label={t('nav_news')}
                  to="/news"
                  testId="news-btn"
                />
              </Box>
            </Group>
          </HeaderLayout.Logo>

          <HeaderLayout.Center>
            <SearchBar />
          </HeaderLayout.Center>

          <HeaderLayout.Actions>
            {/* Mobile (<md): single burger that opens the menu Drawer. */}
            <Burger
              opened={menuOpened}
              onClick={toggleMenu}
              hiddenFrom="md"
              size="sm"
              aria-label={t('main_navigation', 'Navegación principal')}
              data-testid="header-burger"
            />

            {/* Desktop (≥md): the full inline action row, unchanged. */}
            <Group gap="sm" wrap="nowrap" align="center" visibleFrom="md">
            <HeaderPreferences
              currentLanguage={currentLanguage}
              onLanguageChange={(code) => changeLanguage(code as SupportedLanguage)}
              currentCurrency={currentCurrency}
              onCurrencyChange={changeCurrency}
              themeLabels={{ light: t('theme_light'), dark: t('theme_dark') }}
              languageTooltip={t('language')}
              currencyTooltip={t('currency')}
              testId="header-preferences"
            />

            {user ? (
              <>
                <HeaderDivider />

                <HeaderNavButton
                  icon={IconHeart}
                  label={t('favorites.title')}
                  onClick={openFavoritesDrawer}
                  badge={favoritesCount}
                  badgeColor="red"
                  testId="favorites-btn"
                />

                <MessagesHeaderButton />

                <MultiCartIcon />

                <HeaderDivider />

                <HeaderNavButton
                  icon={IconBuildingStore}
                  label={t('my_merchants')}
                  to="/my-merchants"
                  testId="my-merchants-btn"
                />

                <HeaderNavButton
                  icon={IconWallet}
                  label={t('finances')}
                  to="/finances"
                  testId="finances-btn"
                />

                {isAdmin && <HeaderDivider />}

                {isAdmin && (
                  <HeaderNavButton
                    icon={IconListTree}
                    label={t('catalog')}
                    to="/admin/catalog"
                    testId="catalog-btn"
                  />
                )}

                {isAdmin && (
                  <HeaderNavButton
                    icon={IconShield}
                    label={t('admin')}
                    to="/admin/users"
                    testId="admin-btn"
                  />
                )}

                <HeaderDivider />

                <HeaderUserSection
                  user={user}
                  onLogout={handleLogout}
                  profileTooltip={t('profile')}
                  logoutTooltip={t('logout')}
                  logoutConfirmTitle={tAuth('userMenu.logoutConfirmTitle')}
                  logoutConfirmMessage={tAuth('userMenu.logoutConfirmMessage')}
                  cancelLabel={tAuth('userMenu.cancel')}
                  confirmLabel={tAuth('userMenu.logout')}
                  testId="user-section"
                />
              </>
            ) : (
              <>
                <HeaderDivider />
                <Tooltip label={t('login')}>
                <ActionIcon
                  variant="filled"
                  size="lg"
                  onClick={openLogin}
                  aria-label={t('login')}
                  data-testid="login-btn"
                >
                  <IconUser size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              </>
            )}
            </Group>
          </HeaderLayout.Actions>
        </HeaderLayout>
      </MantineAppShell.Header>

      {/* Mobile menu Drawer — mirrors the desktop action row; opened by the
          header burger (<md). Lists every action so nothing is clipped
          off-screen on phones (BUG-CF-HEADER-RESP). */}
      <Drawer
        opened={menuOpened}
        onClose={closeMenu}
        position="right"
        size="80%"
        title={t('menu', 'Menú')}
        hiddenFrom="md"
        data-testid="mobile-menu-drawer"
        zIndex={300}
      >
        <Stack gap={0}>
          {/* Preferences (theme / language / currency) */}
          <Box px="sm" py="xs">
            <HeaderPreferences
              currentLanguage={currentLanguage}
              onLanguageChange={(code) => changeLanguage(code as SupportedLanguage)}
              currentCurrency={currentCurrency}
              onCurrencyChange={changeCurrency}
              themeLabels={{ light: t('theme_light'), dark: t('theme_dark') }}
              languageTooltip={t('language')}
              currencyTooltip={t('currency')}
              testId="mobile-header-preferences"
            />
          </Box>

          <Divider my="xs" />

          <NavLink
            label={t('nav_news')}
            leftSection={<IconSpeakerphone size={20} stroke={1.5} />}
            onClick={() => handleMenuNavigate('/news')}
            data-testid="menu-news"
          />

          {user ? (
            <MobileMenuUserItems
              isAdmin={isAdmin}
              favoritesCount={favoritesCount}
              onNavigate={handleMenuNavigate}
              onCloseMenu={closeMenu}
              onOpenFavorites={openFavoritesDrawer}
              onLogout={handleLogout}
            />
          ) : (
            <NavLink
              label={t('login')}
              leftSection={<IconUser size={20} stroke={1.5} />}
              onClick={() => {
                closeMenu();
                openLogin();
              }}
              data-testid="menu-login"
            />
          )}
        </Stack>
      </Drawer>

      <MantineAppShell.Main>
        <main id="main-content" tabIndex={-1}>{children}</main>
        <Footer />
      </MantineAppShell.Main>

      {/* Login Modal */}
      <LoginModal
        opened={loginOpened}
        onClose={closeLogin}
        onSuccess={handleLoginSuccess}
        showSocialLogin
        facebookAppId={FACEBOOK_APP_ID}
        returnUrl={oauthReturnUrl}
      />

      {/* Register Modal */}
      <RegisterModal
        opened={registerOpened}
        onClose={closeRegister}
        onSuccess={handleRegisterComplete}
        showLogin
        onLoginClick={switchToLogin}
        termsUrl="/terms"
        privacyUrl="/privacy"
        showSocialLogin
        facebookAppId={FACEBOOK_APP_ID}
        returnUrl={oauthReturnUrl}
      />

      {/* Favorites Drawer */}
      <FavoritesDrawer />
    </MantineAppShell>
  );
}
